"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, CheckCircle, ChevronLeft } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ProbeMessage,
  ProbeRequest,
  ProbeResponse,
  ProbeResult,
} from "@/lib/types/probe.types";
import { useEnv } from "@/context/envContext";

const TypingAnimation = ({
  text,
  onComplete,
}: {
  text: string;
  onComplete: () => void;
}) => {
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayedText((prev) => prev + text[currentIndex]);
        setCurrentIndex((prev) => prev + 1);
      }, 10); // Adjust typing speed here (milliseconds per character)
      return () => clearTimeout(timer);
    } else {
      onComplete();
    }
  }, [currentIndex, text, onComplete]);

  return <p className="text-foreground">{displayedText}</p>;
};

const sanitizeInput = (input: string): string => {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/\//g, "&#x2F;");
};

const generateResponse = async (
  message: string,
  chatHistory: ProbeMessage[],
  probeConfig: Omit<ProbeRequest, "message" | "chat_history">,
  apiUrl: string,
  csrfToken: string,
  applicationId?: string
): Promise<ProbeResponse> => {
  try {
    const response = await fetch(`${apiUrl}/api/career/probe`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({
        ...probeConfig,
        message,
        applicationId: applicationId,
        chat_history: JSON.stringify(chatHistory),
      }),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to generate response");
    }

    return response.json();
  } catch (error) {
    console.error("Error generating response:", error);
    throw error;
  }
};

const WelcomeScreenSkeleton = () => (
  <div className="min-h-screen flex items-center justify-center bg-background text-black p-4">
    <div className="max-w-2xl w-full space-y-8">
      <div className="text-center space-y-6">
        {/* Animated brain icon using border and pseudo-elements */}
        <div className="relative w-24 h-24 mx-auto">
          <div className="absolute inset-0 border-4 border-[#164a7a] rounded-full animate-[spin_3s_linear_infinite]" />
          <div className="absolute inset-2 border-4 border-[#164a7a] rounded-full animate-[spin_2s_linear_infinite_reverse]" />
          <div className="absolute inset-4 border-4 border-[#164a7a] rounded-full animate-[spin_4s_linear_infinite]" />
        </div>

        <h2 className="text-2xl font-semibold text-[#164a7a]">
          Preparing Your Interview
        </h2>

        {/* Animated dots */}
        <div className="flex justify-center items-center gap-2">
          <div
            className="w-2 h-2 bg-[#164a7a] rounded-full animate-[bounce_1s_infinite]"
            style={{ animationDelay: "0s" }}
          />
          <div
            className="w-2 h-2 bg-[#164a7a] rounded-full animate-[bounce_1s_infinite]"
            style={{ animationDelay: "0.2s" }}
          />
          <div
            className="w-2 h-2 bg-[#164a7a] rounded-full animate-[bounce_1s_infinite]"
            style={{ animationDelay: "0.4s" }}
          />
        </div>

        <div className="space-y-4 text-center max-w-md mx-auto">
          <p className="text-muted-foreground">
            Our AI interviewer is crafting personalized questions to understand
            your experience and potential.
          </p>
          <p className="text-sm text-muted-foreground">
            This may take a few moments...
          </p>
        </div>
      </div>
    </div>
  </div>
);

const LoadingSkeleton = ({ progress }: { progress: number }) => (
  <>
    <div className="fixed top-0 left-0 w-full h-2 bg-gray-200">
      <div
        className="h-full bg-[#164a7a] transition-all duration-500 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
    <div className="max-w-2xl w-full space-y-8">
      <Skeleton className="h-10 w-32" />
      <div className="space-y-4">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <div className="space-y-4 mt-4">
          <Skeleton className="h-[144px] w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    </div>
  </>
);

// Add IDB interface
interface SavedProbeData {
  jobId: string;
  chatHistory: ProbeMessage[];
  lastUpdated: string;
}

// Add IDB initialization
const initDB = async () => {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open("candidateProbeDB", 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains("probes")) {
        db.createObjectStore("probes", { keyPath: "jobId" });
      }
    };
  });
};

// Add save and load functions
const saveToIDB = async (jobId: string, chatHistory: ProbeMessage[]) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(["probes"], "readwrite");
      const store = transaction.objectStore("probes");

      const saveData: SavedProbeData = {
        jobId,
        chatHistory,
        lastUpdated: new Date().toISOString(),
      };

      const request = store.put(saveData);

      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => resolve(true);
    });
  } catch (error) {
    console.error("Error saving probe data:", error);
  }
};

const loadFromIDB = async (jobId: string): Promise<SavedProbeData | null> => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(["probes"], "readonly");
      const store = transaction.objectStore("probes");
      const request = store.get(jobId);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  } catch (error) {
    console.error("Error loading probe data:", error);
    return null;
  }
};

export function CandidateFeedbackProbeComponent({
  probeConfig,
  jobId,
  onComplete,
  csrfToken,
  applicationId,
}: {
  probeConfig: Omit<ProbeRequest, "message" | "chat_history">;
  jobId: string;
  previewMode?: boolean;
  onComplete?: (result: ProbeResult) => void;
  csrfToken: string;
  applicationId?: string;
}) {
  const env = useEnv();
  const [chatHistory, setChatHistory] = useState<ProbeMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [textInput, setTextInput] = useState("");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [showingPreviousMessages, setShowingPreviousMessages] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isTypingComplete, setIsTypingComplete] = useState(true);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  // Add a ref to track if onComplete has been called
  const onCompleteCalledRef = useRef(false);

  // Modify the useEffect for loading saved chat
  useEffect(() => {
    const loadSavedChat = async () => {
      setIsLoading(true);
      try {
        const savedData = await loadFromIDB(jobId);
        if (savedData?.chatHistory && savedData.chatHistory.length > 0) {
          setChatHistory(savedData.chatHistory);
          setCurrentQuestionIndex(savedData.chatHistory.length - 1);
          setShowingPreviousMessages(false);
          setIsTypingComplete(true);
        } else {
          // Remove startInterview() from here
          setCurrentQuestionIndex(-1); // Set to welcome screen
        }
      } catch (error) {
        console.error("Error loading saved chat:", error);
        setCurrentQuestionIndex(-1); // Set to welcome screen
      } finally {
        setIsLoading(false);
      }
    };

    loadSavedChat();
  }, [jobId]);

  const startInterview = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await generateResponse(
        "Hello",
        [],
        probeConfig,
        env.NEXT_PUBLIC_EXTERNAL_API_URL || "",
        csrfToken,
        applicationId
      );
      if (response.data.chat_history) {
        setChatHistory(response.data.chat_history);
        setCurrentQuestionIndex(0);
        setIsTypingComplete(false);
      }
    } catch (error) {
      console.error("Error starting interview:", error);
    } finally {
      setIsLoading(false);
    }
  }, [probeConfig, env.NEXT_PUBLIC_EXTERNAL_API_URL, csrfToken, applicationId]);

  // Update IDB whenever chat history changes
  useEffect(() => {
    if (chatHistory.length > 0) {
      saveToIDB(jobId, chatHistory);
    }
  }, [chatHistory, jobId]);

  // Clear IDB data when interview is complete
  useEffect(() => {
    if (isComplete) {
      const clearSavedChat = async () => {
        try {
          const db = await initDB();
          const transaction = db.transaction(["probes"], "readwrite");
          const store = transaction.objectStore("probes");
          await store.delete(jobId);
        } catch (error) {
          console.error("Error clearing saved chat:", error);
        }
      };
      clearSavedChat();
    }
  }, [isComplete, jobId]);

  const handleAnswerSubmission = async (answer: string) => {
    const sanitizedAnswer = sanitizeInput(answer);
    setIsLoading(true);
    setShowingPreviousMessages(false);
    setIsTypingComplete(false);
    try {
      const response = await generateResponse(
        sanitizedAnswer,
        chatHistory,
        probeConfig,
        env.NEXT_PUBLIC_EXTERNAL_API_URL || "",
        csrfToken,
        applicationId
      );

      if (
        response.data.is_conversation_stop === "true" ||
        response.data.is_conversation_stop === "yes"
      ) {
        setIsComplete(true);
        setChatHistory(response.data.chat_history);
        setProgress(100);

        // Start countdown from 3
        setCountdown(3);

        // Create countdown timer
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev === null || prev <= 1) {
              clearInterval(timer);
              // When countdown reaches 0, call onComplete only if it hasn't been called yet
              if (
                response.data.result &&
                onComplete &&
                !onCompleteCalledRef.current
              ) {
                // Set the flag to true to prevent multiple calls
                onCompleteCalledRef.current = true;
                onComplete({
                  ...response.data.result,
                  chat_history: response.data.chat_history,
                });
              }
              return null;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setChatHistory(response.data.chat_history);
        // Calculate progress based on number of questions
        // First 10 questions increase by larger amounts, after that smaller increments
        const questionCount = response.data.chat_history.length / 2; // Divide by 2 because each Q&A is 2 messages
        if (questionCount <= 10) {
          setProgress(Math.min(85, questionCount * 8)); // Each of first 10 questions adds 8%
        } else {
          // After 10 questions, divide remaining 15% among next questions
          const remainingProgress = Math.min(
            95 - 80,
            (questionCount - 10) * 1.5
          );
          setProgress(80 + remainingProgress);
        }
      }
    } catch (error) {
      console.error("Error handling answer submission:", error);
    } finally {
      setIsLoading(false);
      setTextInput("");
    }
  };

  const PreviousMessagesView = () => {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          onClick={() => setShowingPreviousMessages(false)}
          className="mb-4"
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back to current question
        </Button>
        <div className="space-y-6">
          {chatHistory
            .filter(
              (message, index) =>
                !(
                  index === 0 &&
                  message.role === "user" &&
                  message.content === "Hello"
                )
            )
            .map((message, index) => (
              <div
                key={index}
                className={`space-y-2 pb-4 border-b last:border-b-0 ${
                  message.role === "assistant"
                    ? "bg-muted/50 p-4 rounded-lg"
                    : ""
                }`}
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>
                    {message.role === "assistant" ? "Interviewer" : "You"}
                  </span>
                </div>
                <p className="text-foreground">
                  {(() => {
                    if (message.role === "assistant") {
                      try {
                        const parsedContent = JSON.parse(message.content);
                        return parsedContent.response;
                      } catch {
                        return message.content;
                      }
                    }
                    return message.content;
                  })()}
                </p>
              </div>
            ))}
        </div>
      </div>
    );
  };

  if (isLoading) {
    if (currentQuestionIndex === -1) {
      return <WelcomeScreenSkeleton />;
    }
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <div className="fixed top-0 left-0 w-full h-2 bg-gray-200">
          <div
            className="h-full bg-[#164a7a] transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <LoadingSkeleton progress={progress} />
        </div>
      </div>
    );
  }

  if (isComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-2xl w-full space-y-8">
          <div className="space-y-4 text-center">
            <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
            <h1 className="text-4xl font-bold tracking-tight">Thank You!</h1>
            {countdown !== null && (
              <div className="relative w-24 h-24 mx-auto my-8">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-full h-full border-4 border-[#164a7a] rounded-full animate-[spin_3s_linear_infinite]" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-4xl font-bold text-[#164a7a]">
                    {countdown}
                  </span>
                </div>
              </div>
            )}
            <p className="text-xl text-muted-foreground">
              Your interview is complete.
            </p>
            <p className="text-muted-foreground">
              We appreciate your time and thoughtful responses. Our team will
              carefully review your answers and get back to you soon with the
              next steps in the process.
            </p>
            <p className="text-muted-foreground">
              If you have any questions or need to provide additional
              information, please don&apos;t hesitate to contact our HR
              department.
            </p>
            <p className="text-muted-foreground">
              Best of luck with your application!
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Welcome screen
  if (currentQuestionIndex === -1) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-2xl w-full space-y-8">
          <div className="space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">
              You&apos;re Almost There! 🎉
            </h1>
            <p className="text-xl text-muted-foreground">
              Ready to showcase your amazing skills and experience? Let&apos;s
              dive into some engaging questions!
            </p>
            <p className="text-muted-foreground">
              Remember, this is your moment to shine - take your time and let
              your expertise speak for itself.
            </p>
          </div>
          <Button onClick={startInterview} size="lg" className="w-full">
            Begin Interview <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  const currentMessage = chatHistory[chatHistory.length - 1];
  const currentQuestionText = (() => {
    if (currentMessage?.role === "assistant") {
      try {
        const parsedContent = JSON.parse(currentMessage.content);
        return parsedContent.response;
      } catch {
        return currentMessage.content;
      }
    }
    return "";
  })();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {currentQuestionIndex >= 0 && !isComplete && (
        <div className="fixed top-0 left-0 w-full h-2 bg-gray-200">
          <div
            className="h-full bg-[#164a7a] transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          {chatHistory.length > 2 && !showingPreviousMessages && (
            <Button
              variant="ghost"
              onClick={() => setShowingPreviousMessages(true)}
              className="mb-4"
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              View Previous Messages
            </Button>
          )}

          {showingPreviousMessages ? (
            <PreviousMessagesView />
          ) : (
            <div className="space-y-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                {!isTypingComplete ? (
                  <TypingAnimation
                    text={currentQuestionText}
                    onComplete={() => setIsTypingComplete(true)}
                  />
                ) : (
                  <p className="text-foreground">{currentQuestionText}</p>
                )}
              </div>

              <div className="space-y-4 mt-4">
                <Textarea
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Type your answer here..."
                  rows={6}
                  className="w-full resize-none"
                  disabled={!isTypingComplete}
                />
                <Button
                  onClick={() => {
                    if (textInput.trim()) {
                      handleAnswerSubmission(textInput);
                    }
                  }}
                  disabled={!textInput.trim() || isLoading || !isTypingComplete}
                  size="lg"
                  className="w-full bg-[#164a7a] hover:bg-[#2d6dad] text-white"
                >
                  Submit Answer <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
